import { useEffect, useRef, useState } from 'react';
import { portalExperimentAssignmentsView } from '../state/views';
import { getSessionId } from '../utils/mixpanel';
import {
  clearActiveExperimentAssignment,
  createControlFallback,
  createSessionAssignmentDescriptor,
  evaluateExperiment,
  setActiveExperimentAssignment,
} from '../service/experiments';

const clearPersistedAssignment = async (experimentKey) => {
  const currentAssignments =
    portalExperimentAssignmentsView.read().state || {};
  const nextAssignments = { ...currentAssignments };
  delete nextAssignments[experimentKey];
  await portalExperimentAssignmentsView.write(nextAssignments);
};

const persistAssignmentDescriptor = async (assignment, isCurrentRequest) => {
  const currentAssignments =
    portalExperimentAssignmentsView.read().state || {};
  if (!isCurrentRequest()) return;
  await portalExperimentAssignmentsView.write({
    ...currentAssignments,
    [assignment.experimentKey]: createSessionAssignmentDescriptor(
      assignment,
      getSessionId(),
    ),
  });
};

const useExperimentAssignment = ({
  experimentKey,
  participant,
  enabled = true,
  expectedVariant = null,
}) => {
  const [state, setState] = useState(() => ({
    assignment: createControlFallback(experimentKey),
    status: enabled ? 'resolving' : 'ready',
  }));
  const participantDebtId = String(participant?.debtId || '').trim();
  const latestRequestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++latestRequestIdRef.current;
    const isCurrentRequest = () =>
      latestRequestIdRef.current === requestId;
    const invalidateCurrentRequest = () => {
      if (isCurrentRequest()) {
        latestRequestIdRef.current += 1;
      }
    };
    const fallback = createControlFallback(experimentKey);
    if (!enabled) {
      setState({
        assignment: fallback,
        status: 'ready',
      });
      return invalidateCurrentRequest;
    }

    setState({
      assignment: fallback,
      status: 'resolving',
    });

    const resolveAssignment = async () => {
      try {
        const assignment = await evaluateExperiment({
          experimentKey,
          participant: {
            ...(participantDebtId ? { debtId: participantDebtId } : {}),
          },
        });
        if (!isCurrentRequest()) return;
        if (assignment.evaluationUnavailable === true) {
          throw new Error('Experiment assignment unavailable');
        }
        if (
          expectedVariant &&
          assignment.participating === true &&
          assignment.variant !== expectedVariant
        ) {
          clearActiveExperimentAssignment(experimentKey);
          await clearPersistedAssignment(experimentKey);
          if (isCurrentRequest()) {
            setState({
              assignment: fallback,
              status: 'ready',
            });
          }
          return;
        }

        setActiveExperimentAssignment(assignment, participantDebtId);
        setState({
          assignment,
          status: 'ready',
        });
        try {
          await persistAssignmentDescriptor(assignment, isCurrentRequest);
        } catch (_persistenceError) {
          // The active session assignment remains authoritative when
          // preference persistence is unavailable.
        }
      } catch (_error) {
        if (!isCurrentRequest()) return;
        clearActiveExperimentAssignment(experimentKey);
        try {
          await clearPersistedAssignment(experimentKey);
        } catch (_cleanupError) {
          // A cleanup failure must not prevent the control fallback.
        }
        if (isCurrentRequest()) {
          setState({
            assignment: {
              ...fallback,
              evaluationUnavailable: true,
            },
            status: 'ready',
          });
        }
      }
    };

    resolveAssignment();
    return invalidateCurrentRequest;
  }, [enabled, expectedVariant, experimentKey, participantDebtId]);

  return state;
};

export default useExperimentAssignment;
